import type { FeedItem, StartedBy } from '../types/domain.types';

export const IN_APP_FRESH_MS = 10 * 60_000;

export const alertKey = (item: FeedItem): string => `${item.issueId ?? '-'}:${item.severity}:${item.step}:${item.at}`;

export interface WaitingCounts {
  readonly pendingCount: number;
  readonly emergencyCount: number;
}

export interface ActiveAlert extends WaitingCounts {
  readonly kind: 'emergency' | 'banner';
  readonly item: FeedItem;
}

export interface ActiveAlerts extends WaitingCounts {
  readonly emergency: FeedItem | null;
  readonly banner: FeedItem | null;
}

const isFresh = (item: FeedItem, openedAt: number): boolean => {
  const at = Date.parse(item.at);
  return !Number.isNaN(at) && at >= openedAt - IN_APP_FRESH_MS;
};

const isUnclaimed = (item: FeedItem): boolean => item.startedBy === null;

export const isStillOpen = (item: FeedItem): boolean => item.issueId === null || (item.nextAction !== null && item.status !== 'RESOLVED');

export const canStartFrom = (item: FeedItem): boolean =>
  item.issueId !== null && isStillOpen(item) && (item.nextAction === 'START' || item.nextAction === 'START_AND_SILENCE');

export const pickActive = (items: readonly FeedItem[], dismissed: ReadonlySet<string>, openedAt: number): ActiveAlerts => {
  const active = items.filter(
    (item) =>
      item.state === 'ALERTED' && isUnclaimed(item) && isStillOpen(item) && isFresh(item, openedAt) && !dismissed.has(alertKey(item)),
  );
  const emergencies = active.filter((item) => item.fullScreenAlarm);
  const banners = active.filter((item) => !item.fullScreenAlarm);
  const emergency = emergencies[0] ?? null;
  const banner = banners[0] ?? null;
  return {
    emergency,
    banner,
    emergencyCount: Math.max(0, emergencies.length - 1),
    pendingCount: emergency === null ? Math.max(0, banners.length - 1) : banners.length,
  };
};

export const activeAlert = (items: readonly FeedItem[], dismissed: ReadonlySet<string>, openedAt: number): ActiveAlert | null => {
  const { emergency, banner, pendingCount, emergencyCount } = pickActive(items, dismissed, openedAt);
  if (emergency !== null) return { kind: 'emergency', item: emergency, pendingCount, emergencyCount };
  if (banner !== null) return { kind: 'banner', item: banner, pendingCount, emergencyCount };
  return null;
};

export const startedElsewhere = (items: readonly FeedItem[], shown: FeedItem): StartedBy | null => {
  if (shown.issueId === null) return null;
  return items.find((item) => item.issueId === shown.issueId && item.startedBy !== null)?.startedBy ?? null;
};
